export type FullscreenMode = "off" | "native" | "controls";

export function createFullscreen(
  element: HTMLElement,
  changed: (_mode: FullscreenMode) => void
) {
  const doc = element.ownerDocument;
  const supported = !!doc.fullscreenEnabled && !!element.requestFullscreen;
  let mode: FullscreenMode = "off";
  let alive = true;
  let pending = false;
  const update = (next: FullscreenMode) => {
    mode = next;
    if (alive) changed(next);
  };
  const onChange = () => {
    if (doc.fullscreenElement === element) update("native");
    else if (mode === "native") update("off");
  };
  const exit = async () => {
    if (doc.fullscreenElement === element) {
      try {
        await doc.exitFullscreen();
      } catch {
        return; // Keep the restore control available if the browser refuses.
      }
    }
    update("off");
  };
  doc.addEventListener("fullscreenchange", onChange);
  return {
    supported,
    get mode() {
      return mode;
    },
    async enter() {
      if (!alive || pending || mode !== "off") return;
      if (!supported) return update("controls");
      pending = true;
      try {
        await element.requestFullscreen({ navigationUI: "hide" });
        if (!alive) await exit();
        else onChange();
      } catch {
        if (alive) update("controls");
      } finally {
        pending = false;
      }
    },
    exit,
    dispose() {
      alive = false;
      doc.removeEventListener("fullscreenchange", onChange);
      void exit();
    },
  };
}
